
<template>
  <!-- Loader displayed when isLoading is true -->
  <div
      v-if="showLoader"
      class="fixed inset-0 flex items-center justify-center bg-gray-500 bg-opacity-50 z-50"
  >
    <svg
        class="animate-spin h-16 w-16 text-white"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
    >
      <circle
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          stroke-width="4"
          fill="none"
      />
    </svg>
  </div>
  <div v-else>
    <div class="relative overflow-x-auto shadow-md sm:rounded-sm mt-5">
      <table class="w-full text-left rtl:text-right text-gray-700">
        <thead>
        <tr class="bg-gray-100">
          <th
              v-for="(header, index) in tableHeaders"
              :key="index"
              scope="col"
              class="px-6 py-3"
          >
            <div class="flex items-center">
              {{ header }}
            </div>
          </th>
        </tr>
        </thead>
        <tbody>
        <tr
            v-for="(item, index) in facilitySchemes"
            :key="index"
            class="border-b"
        >
          <td class="px-6 py-2">{{ index + 1 }}</td>
          <td class="px-6 py-2">{{ item.code }}</td>
          <td class="px-6 py-2">{{ item.name }}</td>
          <td class="px-6 py-2">{{ item.insurance?.ProviderName }}</td>
          <td class="px-6 py-2">{{ item.scheme_type === 1 ? 'Fee For Service' : ( item.scheme_type === 2 ? 'Capitation' : 'Cash') }}</td>
          <td class="px-6 py-2">
            {{ item.copay_type === 1 ? 'Percentage' : ( item.copay_type === 2 ? 'Flat Rate' : 'None') }}
          </td>
          <td class="px-6 py-2">
            {{ item.copay_amount ? item.copay_amount : 'N/A' }}
          </td>
          <td class="px-6 py-2">
            {{ item.status ? item.status.name : 'N/A' }}
          </td>
          <td>
            {{ new Date(item.created_at).toLocaleDateString('en-US', {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit'
            }) }}
          </td>
          <td>
            <div class="flex space-x-2">
              <div>
                <button
                  @click="openEditSchemeModal(item)"
                >
                  <SquarePen stroke="#012169" fill="white" size="24" />
                </button>
              </div>
              <div>
                <button
                    v-if="item.status && item.status.code !== 'ST-019'"
                    @click="triggerDeleteScheme(item.id)"
                >
                  <Trash2 stroke="red" fill="none" size="24"/>
                </button>
              </div>
            </div>
          </td>
        </tr>
        </tbody>
      </table>
    </div>
  </div>
  <PaginationComponent
      v-model:currentPage="currentPage"
      v-model:itemsPerPage="itemsPerPage"
      :totalPages="totalPages"
      :totalItems="totalItems"
      @update:currentPage="goToPage"
      @update:changePageSize="changePageSize"
  />

  <ConfirmationModal :show="showDeleteConfirmationModal"
                     :title="'Delete Scheme'"
                     :message="'Are you sure you want to delete this scheme?'"
                     @confirm="deleteFacilityScheme"
                     @cancel="closeDeleteConfirmationModal"
  />

  <EditSchemeModal :show="showEditSchemeModal" @close="closeEditSchemeModal" :schemeItem="selectedScheme" @updated="fetchFacilitySchemes"/>

</template>

<script setup>
import {onMounted, ref, watch} from 'vue';
import apiClient from "@/services/api";
import {useToast} from "vue-toast-notification";
import PaginationComponent from "@/components/shared/pagination.vue";
import ConfirmationModal from "@/views/modals/shared/ConfirmationModal.vue";
import EditSchemeModal from "@/views/modals/insurance-management/scheme-management/EditSchemeModal.vue";
import { SquarePen } from "lucide-vue-next";
import { Trash2 } from "lucide-vue-next";

// eslint-disable-next-line no-undef
const props = defineProps({
  fetch: Boolean
});
const showLoader = ref(false);
const showEditSchemeModal = ref(false);
const schemeDeleteLoading = ref(false);
const showDeleteConfirmationModal = ref(false);
const facilitySchemes =  ref([]);
const deletingSchemeId = ref(null);
const toast = useToast();
const currentPage = ref(1);
const itemsPerPage = ref(10);
const totalPages = ref(0);
const totalItems = ref(0);
const tableHeaders = [
  'No',
  'Code',
  'Name',
  'Insurance',
  'Scheme Type',
  'Copay Type',
  'Copay Amount',
  'Status',
  'Created At',
  'Actions'
];

const selectedScheme = ref(null);

watch(() => props.fetch, (newValue) => {
  if (newValue === true) {
    fetchFacilitySchemes(); // Trigger function when showModal becomes true
  }
});

const openEditSchemeModal = (scheme) => {
  showEditSchemeModal.value = true;
  selectedScheme.value = scheme;
}

const closeEditSchemeModal = () => {
  showEditSchemeModal.value = false;
}

const goToPage = (pageNumber) => {
  console.log("goToPage", pageNumber);
  currentPage.value = pageNumber;
  fetchFacilitySchemes();
};

const changePageSize = (pageSize) => {
  console.log("change page size", pageSize);
  itemsPerPage.value = pageSize;
  fetchFacilitySchemes();
};

const triggerDeleteScheme = (schemeId) => {
  deletingSchemeId.value = schemeId;
  showDeleteConfirmationModal.value = true;
}

const closeDeleteConfirmationModal = () => {
  showDeleteConfirmationModal.value = false;
  deletingSchemeId.value = null;
}

const fetchFacilitySchemes = async () => {
  try {

    showLoader.value = true;

    let response = await apiClient.fetchFacilitySchemeList({
      page: currentPage.value,
      itemsPerPage: itemsPerPage.value
    });
    if (response.success) {
      facilitySchemes.value = response.data;
      totalItems.value = response.meta.total_items; // Assuming meta data from backend
      totalPages.value = response.meta.total_pages; // Assuming meta data from backend
      itemsPerPage.value = response.meta.per_page;
    } else {
      console.error("Failed to fetch system billing list:", response.message);
      toast.error(response.message);
    }
  }catch (e) {
    console.error("Failed to fetch system billing list:", e);
    toast.error("Failed to fetch system billing list");
  } finally {
    showLoader.value = false;
  }
}

const deleteFacilityScheme = async () => {
  try {
    schemeDeleteLoading.value = true;
    let response = await apiClient.deleteScheme(deletingSchemeId.value);
    if (response.success) {
      fetchFacilitySchemes();
      toast.success("Scheme deleted successfully");
      showDeleteConfirmationModal.value = false;
    } else {
      showDeleteConfirmationModal.value = false;
      console.error("Failed to delete scheme:", response.message);
      toast.error(response.message);
    }
  }catch (e) {
    showDeleteConfirmationModal.value = false;
    schemeDeleteLoading.value = false;
   toast.error("Failed to delete scheme");
  } finally {
    schemeDeleteLoading.value = false;
  }
}
onMounted(() => {
  fetchFacilitySchemes();
});

</script>

<style scoped>

</style>